// src/components/looker/LookerTileGrid.tsx
/**
 * The /looker tile grid in a workspace's own order and widths. In edit mode a
 * tile can be dragged onto another tile's place, moved with buttons (keyboard
 * and touch, where HTML5 drag does not reach), and switched between the four
 * widths. It only reports the new layout; saving is the page's job.
 *
 * @module components/looker/LookerTileGrid
 */
import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LookerTileCard } from './LookerTile';
import type { LookerImageRequest } from '@/services/looker/lookerApi';
import { isHiddenWhenEmpty, type AppTileState } from '@/hooks/useLookerAppTiles';
import type { LookerTile, LookerTileWidth } from '@/services/looker/lookerTypes';
import { allowsSixth, fitWidth, moveEntry, nextWidth, setWidth, type LookerLayoutEntry } from '@/services/looker/lookerLayout';
import type { CoverageState } from '@/hooks/useLookerCoverage';
import { badgeCoverageOf, tileNotesOf } from '@/services/looker/lookerTileNotes';
import { coverageLabelsFrom } from '@/services/looker/lookerCoverageLabels';

interface Props {
  tiles: readonly LookerTile[];
  layout: readonly LookerLayoutEntry[];
  editing: boolean;
  onChange: (layout: LookerLayoutEntry[]) => void;
  imageRequest?: LookerImageRequest;
  /** App tiles' states by tile id (useLookerAppTiles). */
  appStates?: Readonly<Record<string, AppTileState>>;
  /**
   * Draw a hidden-when-empty app tile with its note instead of dropping it,
   * without the arranging controls (the builder preview). /looker leaves it off.
   */
  showHiddenNote?: boolean;
  /** The page's coverage (useLookerCoverage): Result badges and box 8. Absent = no Result badges, no box 8 section. */
  coverage?: CoverageState;
  /** The one selected objective (payload.objective); box 3's card badge reads it. */
  objective?: string | null;
}

const CONTROL_CLASS =
  'rounded-md border border-neutral-300 bg-white px-2 py-1 text-xs font-medium text-ink-900 hover:bg-neutral-100 disabled:opacity-40';

/**
 * 12 columns from md. A sixth is half a row on a phone, a third (three across)
 * on a tablet and on paper, and a true sixth (six across) only from xl.
 */
const SPAN: Record<LookerTileWidth, string> = {
  sixth: 'col-span-1 md:col-span-4 xl:col-span-2 print:col-span-4',
  third: 'col-span-2 md:col-span-4 print:col-span-4',
  half: 'col-span-2 md:col-span-6 print:col-span-6',
  full: 'col-span-2 md:col-span-12 print:col-span-12',
};

export const LookerTileGrid: React.FC<Props> = ({ tiles, layout, editing, onChange, imageRequest, appStates = {}, showHiddenNote = false, coverage, objective = null }) => {
  const { t } = useTranslation();
  // Naming chips need no read; Result badges only from a ready coverage read, for the selected channels.
  const channels = imageRequest?.channels;
  const notesById = useMemo(() => {
    const data = badgeCoverageOf(coverage, channels);
    const labels = coverageLabelsFrom(t);
    return new Map(tiles.map((tile) => [tile.id, tileNotesOf(tile, data, objective, labels)]));
  }, [tiles, coverage, channels, objective, t]);
  // Literal keys, so the locale integrity test sees them.
  const widthLabel: Record<LookerTileWidth, string> = {
    sixth: t('looker.layout.widths.sixth'),
    third: t('looker.layout.widths.third'),
    half: t('looker.layout.widths.half'),
    full: t('looker.layout.widths.full'),
  };
  const [dragging, setDragging] = useState<string | null>(null);
  const byId = new Map(tiles.map((tile) => [tile.id, tile]));
  // A hidden-when-empty app tile is dropped only here: it keeps its layout entry, so a
  // save while it is hidden keeps its place (ruling 4). Arranging and the builder preview still show it.
  // App boxes never apply the channel filter; they say so while one is on.
  const channelFiltered = (imageRequest?.channels?.length ?? 0) > 0;
  const noteHidden = editing || showHiddenNote;
  const placed = layout.filter((e) => byId.has(e.id) && (noteHidden || !isHiddenWhenEmpty(appStates[e.id])));

  const dropOn = (targetIndex: number, draggedId: string) => {
    const from = layout.findIndex((e) => e.id === draggedId);
    if (from >= 0) onChange(moveEntry(layout, from, targetIndex));
  };

  return (
    <div className="grid grid-cols-2 gap-4 md:grid-cols-12 print:grid-cols-12">
      {placed.map((entry, index) => {
        const tile = byId.get(entry.id) as LookerTile;
        const width = fitWidth(entry.width, tile);
        const editProps = editing
          ? {
              draggable: true,
              onDragStart: (e: React.DragEvent) => {
                e.dataTransfer.setData('text/plain', entry.id);
                e.dataTransfer.effectAllowed = 'move';
                setDragging(entry.id);
              },
              onDragEnd: () => setDragging(null),
              onDragOver: (e: React.DragEvent) => {
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
              },
              onDrop: (e: React.DragEvent) => {
                e.preventDefault();
                const id = e.dataTransfer.getData('text/plain') || dragging;
                setDragging(null);
                if (id && id !== entry.id) dropOn(index, id);
              },
            }
          : {};
        return (
          <div
            key={entry.id}
            data-looker-cell={entry.id}
            className={[
              SPAN[width],
              // The grid stretches every cell to its row; the column lets the card fill it.
              'flex flex-col',
              // On paper a table always takes the full row (half a sheet cannot hold
              // a campaign name) and may run onto the next sheet; a chart never splits.
              tile.type === 'table' ? 'print:col-span-12' : '',
              tile.type === 'table' ? '' : 'break-inside-avoid',
              editing ? 'cursor-move rounded-2xl outline-dashed outline-2 outline-offset-2' : '',
              // The tile being dragged is marked by its outline, never by dimming its text.
              editing ? (dragging === entry.id ? 'outline-ink-950' : 'outline-neutral-400') : '',
            ].join(' ').trim()}
            {...editProps}
          >
            {editing && (
              <div className="mb-2 flex flex-wrap items-center gap-2">
                <button type="button" className={CONTROL_CLASS} disabled={index === 0}
                  aria-label={t('looker.layout.moveEarlier', { title: tile.title })}
                  onClick={() => onChange(moveEntry(layout, layout.indexOf(entry), layout.indexOf(entry) - 1))}>←</button>
                <button type="button" className={CONTROL_CLASS} disabled={index === placed.length - 1}
                  aria-label={t('looker.layout.moveLater', { title: tile.title })}
                  onClick={() => onChange(moveEntry(layout, layout.indexOf(entry), layout.indexOf(entry) + 1))}>→</button>
                <button type="button" className={CONTROL_CLASS}
                  aria-label={t('looker.layout.changeWidth', { title: tile.title, width: widthLabel[width] })}
                  onClick={() => onChange(setWidth(layout, entry.id, nextWidth(width, allowsSixth(tile))))}>
                  {widthLabel[width]}
                </button>
              </div>
            )}
            {noteHidden && isHiddenWhenEmpty(appStates[entry.id]) && (
              <p className="mb-2 text-xs font-medium text-neutral-800">{t('looker.app.hiddenWhenEmpty')}</p>
            )}
            {/* While arranging, a click on a chart must not start a drill or a hover card. */}
            <div className={`flex min-h-0 flex-1 flex-col ${editing ? 'pointer-events-none select-none' : ''}`.trim()}>
              <LookerTileCard tile={tile} imageRequest={imageRequest} width={width} appState={appStates[entry.id]} channelFiltered={channelFiltered}
                notes={notesById.get(entry.id)} coverage={coverage} />
            </div>
          </div>
        );
      })}
    </div>
  );
};
